import React, { useEffect } from 'react';
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Typography, Link, Box, Stack, Backdrop, CircularProgress, Divider } from '@mui/material';
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { parseReturnUrl } from '../../functions';
import { ApplicationModel, RedirectErrorType } from '../../interfaces';
import { ADMIN_APP } from '../../constants';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';
import { getCurrentUser, getCurrentUserApplications, userApplicationSelected } from '../../app/currentUserSlice';
import authService from '../../services/authService';

export default function Client() {
    const [searchParams] = useSearchParams();
    const applications = useCache(CachedEntity.Application);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userState = useAppSelector(store => store.currentUser);
    const [selectedApp, setSelectedApp] = React.useState<string>('');

    useEffect(() => {
        if (userState.redirectError?.appId === ADMIN_APP) {
            setSelectedApp('LogisticsPlus Admin');
        } else {
            const app = applications.find(app => app.key == userState.redirectError?.appId)?.label;
            setSelectedApp(app || '');
        }
    }, [applications]);

    useEffect(() => {
        if (userState.data === undefined && !userState.pending) {
            dispatch(getCurrentUser());
        }

        const redirectUrl: string = parseReturnUrl(searchParams.get("returnUrl"));
        if (redirectUrl) {
            navigate({
                pathname: '/client/redirect',
                search: `?${createSearchParams({ returnUrl: redirectUrl })}`
            });
        }
    }, []);

    const handleProfileClick = () => {
        navigate('/account');
    };

    const handleApplicationClick = (application: ApplicationModel) => {
        dispatch(userApplicationSelected(application));
        navigate({
            pathname: '/client/redirect',
            search: `?${createSearchParams({ returnUrl: application.clientUri })}`
        });
    };

    const handleSignOut = async () => {
        await authService.logout();
    };

    return (
        <React.Fragment>
            <Box width={1}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h4" color="primary" mb={1.5}>You are logged in</Typography>
                        {(userState.data) &&
                            <Typography variant="body1">
                                <Link href="" onClick={handleProfileClick}>{`${userState.data.firstName} ${userState.data.lastName}`}</Link>
                            </Typography>
                        }
                    </Box>
                    {userState.redirectError && applications.length &&
                    <Box>
                        {userState.redirectError.type == RedirectErrorType.unauthorized && <Typography variant="body1" color="error" mb={1.5}>You currently do not have access to {selectedApp}. For further assistance, please contact support or your administrator. </Typography>}
                        <Divider>OR</Divider>
                    </Box>}
                    <Typography variant="body1">Select an application to sign in</Typography>
                    {userState.userApplications.map((d, i) => (
                        <Link id={`${d.clientId.toLowerCase()}-application-link`} href="" onClick={() => { handleApplicationClick(d); }} key={i}>
                            <Box component='img' src={d.logoUri} alt={d.description} maxWidth={180} maxHeight={40} />
                        </Link>
                    ))}
                    <Typography variant="body1" pt={1}>
                        <Link id="application-selection-signout-link" href="#" onClick={handleSignOut}>Sign Out</Link>
                    </Typography>
                </Stack>
            </Box>
            <Backdrop open={userState.pending} className="loading">
                <CircularProgress />
            </Backdrop>
        </React.Fragment>
    );
}
